import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subject, of, switchMap, takeUntil } from 'rxjs';
import { CacheResult, MenuCacheService } from '../../features/table-connect/menu/menu-cache.service';
import { PublicTableAccessResponse } from '../../features/table-connect/guest-landing/guest-landing.service';

/**
 * TC-THEME — runtime applier for the TableConnect guest-surface brand
 * colour.
 *
 * **What it does.** When `bindToken(token)` is called, this service
 * subscribes to {@link MenuCacheService.token$} and, on every successful
 * resolve, writes the admin-chosen `storeThemePrimaryColor` (a
 * `#RRGGBB` hex) into the document-root `--tc-primary` CSS variable.
 * Because `--tc-primary-hover` and `--tc-primary-soft` are derived
 * via `color-mix(in srgb, var(--tc-primary), …)` in `styles.scss`, the
 * press-state and pale-wash shades follow automatically — the admin
 * only ever picks one swatch.
 *
 * **Why this lives at document.documentElement.** TableConnect uses
 * three shared components (TcButton, TcFooter, TcBackButton) with
 * `ViewEncapsulation.None`, plus 8 page-level components that read
 * `var(--tc-primary)` via the cascade. Setting the variable on the
 * `<html>` element makes every consumer see the override with zero
 * per-component wiring.
 *
 * **Lifecycle — exactly ONE token is live at a time.** `bindToken(token)`
 * is idempotent for the token already bound, and *switches* away from any
 * previous one. That switch is the whole point: a guest who reaches a store
 * through the tenant store selector (`/s/:tenantToken`) can go back and pick
 * a different store, and the two stores' brands must not fight over
 * `--tc-primary`. The previous store's `MenuCacheService` feed keeps its
 * periodic refresh timer running (other cached data still depends on it), so
 * a plain per-token subscription would keep re-applying the OLD store's
 * colour on every tick and the new store's brand would never stick. `switchMap`
 * over the active token drops the old feed instead of accumulating feeds.
 *
 * `unbind()` clears the override when the guest leaves store scope entirely
 * (the tenant selector at `/s/...`, or a 404), so a stale brand colour never
 * bleeds onto a tenant-level screen.
 *
 * **Fallback.** A null `storeThemePrimaryColor` clears the inline
 * override, so the `--tc-primary` in `:root` (the iPOS cardinal
 * `#C41E3A`) wins via the cascade. Identical to "no admin override
 * set" by design.
 *
 * **Browser support.** Setting an inline CSS variable on an element
 * works everywhere CSS variables work (IE11 not supported, but that's
 * been irrelevant for years). The `color-mix(...)` derivation of the
 * hover/soft shades needs Chrome 111+ / Firefox 113+ / iOS Safari
 * 16.2+ — see the styles.scss :root comment for the graceful-degrade
 * story on older devices.
 */
@Injectable({ providedIn: 'root' })
export class TcThemeService implements OnDestroy {
  private menuCache = inject(MenuCacheService);
  private destroy$ = new Subject<void>();

  /** The store whose brand is currently on the document root. Null outside
   *  store scope (tenant selector / 404). */
  private activeToken = new BehaviorSubject<string | null>(null);

  /** Last colour written to the document root — used so we only call
   *  the (cheap but observable) `setProperty` when the value changes. */
  private appliedColor: string | null = null;

  constructor() {
    // switchMap, not merge: binding a new token must ABANDON the previous
    // store's feed. That feed stays alive inside MenuCacheService (its
    // periodic timer keeps ticking for other cached data), so anything less
    // than an unsubscribe lets the old store's colour win the next tick.
    this.activeToken
      .pipe(
        switchMap(token => token ? this.menuCache.token$(token) : of(null)),
        takeUntil(this.destroy$))
      .subscribe(result => this.onResolve(result));
  }

  /**
   * Make `token` the active store and apply its primary colour to the
   * document root. Called from AppComponent's NavigationEnd handler on every
   * TableConnect route; repeated calls for the same token are no-ops.
   */
  bindToken(token: string): void {
    if (!token || this.activeToken.value === token) return;

    // Switching stores — drop the previous brand immediately rather than
    // leaving it on screen until the new resolve lands. The default iPOS red
    // for a moment is honest; another store's brand is not.
    if (this.activeToken.value !== null) this.clearColor();

    // Ensure the resolve actually fires — bindToken runs from
    // AppComponent's NavigationEnd handler on every TableConnect route,
    // including `/payment-result` and `/receipt` where the full menu
    // cache is genuinely unnecessary. Use the lighter `bindTokenOnly`
    // so we only trigger `refreshToken`, not the categories + items
    // fetches that the full `bind` would also kick off. A menu-data
    // page (landing / menu / cart / checkout / bill) still calls full
    // `bind()` from its own ngOnInit, which spins up the periodic
    // refresh timer and pulls categories + items on arrival.
    this.menuCache.bindTokenOnly(token);
    this.activeToken.next(token);
  }

  /**
   * Leave store scope — used for the tenant store selector and the 404, which
   * are not any one store's surface. Reverts to the cascaded iPOS default.
   */
  unbind(): void {
    if (this.activeToken.value === null) return;
    this.activeToken.next(null);
    this.clearColor();
  }

  private onResolve(result: CacheResult<PublicTableAccessResponse> | null): void {
    if (!result || result.status !== 'ok') return;
    this.applyColor(result.data.storeThemePrimaryColor ?? null);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearColor();
  }

  /** Direct setter for tests / programmatic use. Production code should
   *  bind via {@link bindToken} so the colour stays in sync with the
   *  cached resolve response. */
  applyColor(color: string | null): void {
    if (this.appliedColor === color) return;

    // Guard against non-browser execution (SSR / unit tests without
    // jsdom set up). `document` exists in jest-preset-angular's jsdom
    // env, but a future SSR target would not have it.
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    if (color) {
      root.style.setProperty('--tc-primary', color);
    } else {
      // null = revert to the cascaded :root value (the iPOS default).
      root.style.removeProperty('--tc-primary');
    }
    this.appliedColor = color;
  }

  private clearColor(): void {
    if (typeof document === 'undefined') return;
    document.documentElement.style.removeProperty('--tc-primary');
    this.appliedColor = null;
  }
}
